// MultiImageDropzone.tsx
import React, { useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableImageItem from './SortableImageItem';
import { ProductoImagesI } from '../../../models/productos';

interface MultiImageDropzoneProps {
  imagenes: ProductoImagesI[];
  setImagenes: (imagenes: ProductoImagesI[]) => void;
  onRemove: (index: number) => void;
  title: string;
  handleImageFieldChange: (index: number, field: "titulo" | "descripcion" | "archivo", value: string | File, name: "productos_imagenes" | "productos_imagenes_pequenas") => void
  name: "productos_imagenes" | "productos_imagenes_pequenas"
}

const MultiImageDropzone: React.FC<MultiImageDropzoneProps> = ({
  imagenes,
  setImagenes,
  onRemove,
  title,
  handleImageFieldChange,
  name
}) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files).filter((file) =>
        file.type.startsWith('image/')
      );

      const nuevas = files.map((file, i) => ({
        archivo:file ,
        archivoUrl: URL.createObjectURL(file),
        titulo: '',
        descripcion: '',
        id: file.name + '-' + Math.random().toString(36).substring(2, 9),
        orden: imagenes.length + i,
      }));

      setImagenes([...imagenes, ...nuevas]);
    },
    [imagenes, setImagenes]
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleInputChange = (
    index: number,
    key: 'titulo' | 'descripcion',
    value: string
  ) => {
    const updated = [...imagenes];
    updated[index][key] = value;
    setImagenes(updated);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = imagenes.findIndex((i) => i.id === active.id);
      const newIndex = imagenes.findIndex((i) => i.id === over.id);
      const reordenadas = arrayMove(imagenes, oldIndex, newIndex);

      // Reasignar el campo 'orden' según la nueva posición
      const conOrden = reordenadas.map((img, i) => ({ ...img, orden: i }));
      setImagenes(conOrden);
    }
  };

  

  return (
    <div>
      <label>{title}(arrástralas aquí)</label>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        style={{
          border: '2px dashed #ccc',
          padding: '20px',
          marginBottom: '20px',
          textAlign: 'center',
          borderRadius: '8px',
        }}
      >
        Suelta aquí tus imágenes
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={imagenes.map((img) => img.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {imagenes.map((imagen, index) => (
              <SortableImageItem
                key={imagen.id}
                imagen={imagen}
                index={index}
                onRemove={onRemove}
                handleInputChange={handleInputChange}
                name={name}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default MultiImageDropzone;
